import { useRef, useState, useEffect, useMemo } from 'react';
import HighchartsReact from 'highcharts-react-official';
import defaultsDeep from 'lodash/defaultsDeep';
import omit from 'lodash/omit';
import { useResizeDetector } from 'react-resize-detector';
import { logger, toDataTimeSeries } from '../../utils';
import { HIGHCHARTS } from './HIGHCHARTS';
import { SeriesType } from '../components/cartesian';
import { isArray } from 'lodash';
import EmptyBox from './EmptyBox';
import { Layout, css } from '@appd/uik';

const defaultOptions = {
  accessibility: { enabled: false },
  chart: {
    animation: false,
    backgroundColor: 'rgba(0,0,0,0)',
    plotBackgroundColor: null,
    plotBorderWidth: null,
    plotShadow: false,
    spacing: [10, 5, 5, 5],
    style: {
      fontFamily: 'var(--typography-font-body)',
    },
    styledMode: false,
  },
  colors: [
    'var(--color-accent-a)',
    'var(--color-accent-b)',
    'var(--color-accent-c)',
    'var(--color-accent-d)',
    'var(--color-accent-e)',
    'var(--color-accent-f)',
    'var(--color-accent-g)',
    'var(--color-accent-h)',
    'var(--color-accent-i)',
    'var(--color-accent-j)',
  ],
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  lang: {
    noData: 'No Data',
  },
  legend: {
    enabled: false,
    itemHoverStyle: {
      color: 'var(--color-default-text)',
    },
    itemStyle: {
      backgroundColor: 'var(--color-default-bg)',
      color: 'var(--color-default-text-medium)',
      fontFamily: 'var(--typography-font-body)',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '18px',
    },
    navigation: {
      activeColor: 'var(--color-default-text-medium)',
      arrowSize: 10,
      inactiveColor: 'var(--color-default-text-medium-disabled)',
      style: {
        color: 'var(--color-default-text-medium)',
        fontFamily: 'var(--typography-font-body)',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
      },
    },
    symbolRadius: 0,
  },
  plotOptions: {
    series: {
      minPointLength: 3,
    },
  },
  subtitle: {
    text: '',
  },
  time: {
    useUTC: false,
  },
  title: {
    text: '',
  },
  tooltip: {
    backgroundColor: 'var(--color-default-bg)',
    outside: true,
    style: {
      backgroundColor: 'var(--color-default-bg)',
      color: 'var(--color-default-text)',
      fontFamily: 'var(--typography-font-body)',
      fontSize: '11px',
      fontStyle: 'normal',
      fontWeight: '400',
    },
    useHTML: true,
    valueDecimals: 0,
  },
  xAxis: {
    labels: {
      distance: 5,
      style: {
        backgroundColor: 'var(--color-default-bg)',
        color: 'var(--color-default-text-medium)',
        fontFamily: 'var(--typography-font-body)',
        fontSize: '11px',
        fontStyle: 'normal',
        fontWeight: '400',
      },
    },
  },
  yAxis: {
    labels: {
      distance: 5,
      style: {
        backgroundColor: 'var(--color-default-bg)',
        color: 'var(--color-default-text-medium)',
        fontFamily: 'var(--typography-font-body)',
        fontSize: '11px',
        fontStyle: 'normal',
        fontWeight: '400',
      },
    },
  },
};

interface SizeAwareDashifyHighchartsProps {
  /** Inherited from [`<HighchartsReact>`](https://www.npmjs.com/package/highcharts-react-official#options-details). */
  containerProps?: JSX.IntrinsicElements['div'];
  width: number | undefined;
  height: number;
  series?: SeriesType | SeriesType[] | undefined;
  restOptions?: Record<string, unknown>;
}

const SizeAwareDashifyHighcharts = ({
  containerProps: {
    children: _children,
    dangerouslySetInnerHTML: _dangerouslySetInnerHTML,
    ref: _ref,
    suppressContentEditableWarning: _suppressContentEditableWarning,
    suppressHydrationWarning: _suppressHydrationWarning,
    security: _security,
    ...containerProps
  } = {},
  width,
  height,
  series,
  ...restOptions
}: SizeAwareDashifyHighchartsProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const Highcharts = HIGHCHARTS.use();
  const seriesLength = useMemo(
    () =>
      isArray(series)
        ? series.reduce((acc, curr) => (acc = acc + (curr?.data?.length ?? 0)), 0)
        : series?.data?.length ?? 0,
    [series],
  );

  const [options, setOptions] = useState(defaultsDeep({}, restOptions, defaultOptions));

  useEffect(() => {
    if (chartRef?.current?.chart) {
      chartRef.current.chart.reflow(); // only fits to width of container (https://api.highcharts.com/highcharts/chart.reflow)
      logger.log('Chart', chartRef.current.chart);
    }
  }, [height, width]);

  useEffect(() => {
    if (!Highcharts) {
      return;
    }

    // TODO: Only support series as an array (need to modify existing templates, enforce in editor experience)
    if (series) {
      const seriesData =
        options?.xAxis?.type === 'datetime'
          ? Array.isArray(series)
            ? series.map(s => toDataTimeSeries(s))
            : toDataTimeSeries(series)
          : series;
      const nullFreeSeriesData = Array.isArray(seriesData)
        ? seriesData.filter(item => item.name !== null && (item as SeriesType).data !== undefined)
        : [seriesData]; // series passed to options should be an array
      logger.log('SeriesData', series, nullFreeSeriesData);
      setOptions(defaultsDeep({}, { series: nullFreeSeriesData }, restOptions, omit(options, ['series'])));
    }
  }, [series, Highcharts]);

  // Highcharts will not render if options.series is undefined
  if (!options.series) {
    return null;
  }

  if (seriesLength === 0) {
    return <EmptyBox />;
  }

  return (
    <HighchartsReact
      containerProps={{
        ...containerProps,
        className: css(
          containerProps?.className,
          (options?.legend?.enabled || options?.plotOptions?.series?.point?.events?.click) && Layout.$.ACTIONABLE,
        ),
        style: {
          contain: 'size', // DASHUI-6921
          height: height ?? '100%', // use explicit height or 100% to fit container height
          ...containerProps?.style,
        },
      }}
      highcharts={Highcharts}
      ref={chartRef}
      options={options}
    />
  );
};

interface DashifyHighchartsProps extends Record<string, unknown> {
  /** Inherited from [`<HighchartsReact>`](https://www.npmjs.com/package/highcharts-react-official#options-details). */
  containerProps?: JSX.IntrinsicElements['div'];
  width?: number | undefined;
  height: number;
  series?: SeriesType | SeriesType[] | undefined;
}

const DashifyHighcharts = (props: DashifyHighchartsProps) => {
  const { width, ref } = useResizeDetector();
  const containerWidth = props.width ?? width;
  return (
    <div ref={ref} className="h-full" data-test="SizeAwareDashHighchart">
      {containerWidth !== null ? <SizeAwareDashifyHighcharts {...props} width={containerWidth} /> : ''}
    </div>
  );
};

export { DashifyHighcharts };
